import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import axios from "axios";
import API_BASE_URL from "../config";

const TaxRulesAdmin = () => {
  const [rules, setRules] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    country_id: "",
    name: "",
    type: "flat",
    rate: "",
    slabs: '[{"min": 0, "max": null, "rate": 0}]',
  });

  useEffect(() => {
    fetchCountries();
    fetchRules();
  }, []);

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/salary_template_countries`);
      setCountries(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/tax_rules`);
      setRules(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = {
        country_id: parseInt(form.country_id),
        name: form.name,
        type: form.type,
      };

      if (form.type === "flat") {
        payload.rate = parseFloat(form.rate);
      } else {
        try {
          JSON.parse(form.slabs); // Validate JSON
          payload.slabs = form.slabs;
        } catch {
          throw new Error("Invalid JSON in slabs");
        }
      }

      if (editingRule) {
        await axios.put(`${API_BASE_URL}/tax_rules/${editingRule.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/tax_rules`, payload);
      }

      setShowModal(false);
      resetForm();
      fetchRules();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tax rule?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/tax_rules/${id}`);
      fetchRules();
    } catch (err) {
      alert("Failed to delete");
    }
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      country_id: rule.country_id,
      name: rule.name,
      type: rule.type,
      rate: rule.rate || "",
      slabs: rule.slabs ? JSON.stringify(JSON.parse(rule.slabs), null, 2) : '[{"min": 0, "max": null, "rate": 0}]',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingRule(null);
    setForm({
      country_id: "",
      name: "",
      type: "flat",
      rate: "",
      slabs: '[{"min": 0, "max": null, "rate": 0}]',
    });
  };

  const openAdd = () => {
    resetForm();
    setShowModal(true);
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Tax Rules Admin</h3>
        <Button onClick={openAdd}>Add Rule</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Country</th>
            <th>Name</th>
            <th>Type</th>
            <th>Rate</th>
            <th>Slabs</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <tr key={rule.id}>
              <td>
                <Badge bg="secondary">
                  {countries.find((c) => c.id === rule.country_id)?.country_name || "N/A"}
                </Badge>
              </td>
              <td>{rule.name}</td>
              <td><Badge bg={rule.type === "flat" ? "success" : "info"}>{rule.type}</Badge></td>
              <td>{rule.rate !== null ? `${rule.rate}%` : "—"}</td>
              <td>
                {rule.slabs ? (
                  <small>
                    {JSON.parse(rule.slabs).map((s, i) => (
                      <div key={i}>
                        {s.min} – {s.max ?? "∞"}: <strong>{s.rate}%</strong>
                      </div>
                    ))}
                  </small>
                ) : "—"}
              </td>
              <td>
                <Button size="sm" onClick={() => openEdit(rule)} className="me-2">
                  Edit
                </Button>
                <Button size="sm" variant="danger" onClick={() => handleDelete(rule.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingRule ? "Edit" : "Add"} Tax Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Select
                value={form.country_id}
                onChange={(e) => setForm({ ...form, country_id: e.target.value })}
                required
              >
                <option value="">Select Country</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.country_name} ({c.country_code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value, rate: "", slabs: '[{"min": 0, "max": null, "rate": 0}]' })}
              >
                <option value="flat">Flat Rate</option>
                <option value="slab">Slab (Tiered)</option>
              </Form.Select>
            </Form.Group>

            {form.type === "flat" ? (
              <Form.Group className="mb-3">
                <Form.Label>Flat Rate (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  required
                />
              </Form.Group>
            ) : (
             <Form.Group className="mb-3">
  <Form.Label>Slabs (JSON)</Form.Label>
  <Form.Control
    as="textarea"
    rows={6}
    value={form.slabs}
    onChange={(e) => setForm({ ...form, slabs: e.target.value })}
    placeholder='[{"min": 0, "max": 500000, "rate": 5}, ...]'
  />
  <Form.Text>
    Format: <code>{"[{\"min\": 0, \"max\": 500000, \"rate\": 5}]"}</code>
  </Form.Text>
</Form.Group>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Save"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TaxRulesAdmin;